/**
 * MirrorEditModals.jsx — Batch #14.218
 *
 * Modals admin pour éditer (au nom du client) depuis la « Vue portail » :
 *  - EditBuildingModal  (PUT /api/admin/immeuble/{master_id}/buildings/{id})
 *  - EditUnitModal      (PUT /api/admin/immeuble/{master_id}/units/{id})
 *  - EditTenantModal    (PUT /api/admin/immeuble/{master_id}/tenants/{id})
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { Loader2, X, Save, Building2, Home, User } from "lucide-react";
import { API, authH } from "@/api";


function Field({ label, children, span2 = false }) {
  return (
    <label className={`block ${span2 ? "col-span-2" : ""}`}>
      <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

function ModalShell({ title, icon: Icon, tone, onClose, onSubmit, busy, children, testid }) {
  const tones = {
    blue: "from-blue-600 to-blue-700",
    emerald: "from-emerald-600 to-emerald-700",
    indigo: "from-indigo-600 to-indigo-700",
  };
  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className={`px-5 py-3 bg-gradient-to-r ${tones[tone] || tones.blue} text-white flex items-center justify-between sticky top-0 z-10`}>
          <h3 className="text-base font-bold inline-flex items-center gap-2"><Icon className="w-5 h-5" /> {title}</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">{children}</div>
        <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-end gap-2 sticky bottom-0">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border" data-testid={`${testid}-cancel`}>Annuler</button>
          <button onClick={onSubmit} disabled={busy}
            className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid={`${testid}-save`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// EDIT BUILDING
// ════════════════════════════════════════════════════════════════════════════
export function EditBuildingModal({ masterId, building, primaryClientId, onClose, onSaved }) {
  const isCreate = !building;
  const [form, setForm] = useState({
    name: building?.name || "",
    address_line: building?.address_line || "",
    city: building?.city || "",
    postal_code: building?.postal_code || "",
    province: building?.province || "QC",
    property_type: building?.property_type || "",
    nb_units_total: building?.nb_units_total ?? "",
    year_built: building?.year_built ?? "",
    notes: building?.notes || "",
    owner_legal_name: building?.owner_legal_name || "",
    owner_address_line: building?.owner_address_line || "",
    owner_city: building?.owner_city || "",
    owner_province: building?.owner_province || "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (isCreate && !form.name.trim()) {
      toast.error("Le nom de l'immeuble est requis");
      return;
    }
    setBusy(true);
    try {
      const body = { ...form };
      ["year_built", "nb_units_total"].forEach((k) => {
        if (body[k] === "") delete body[k];
        else body[k] = Number(body[k]);
      });
      Object.keys(body).forEach((k) => { if (body[k] === "") delete body[k]; });
      let r;
      if (isCreate) {
        if (!primaryClientId) {
          toast.error("client_id du propriétaire principal manquant");
          setBusy(false);
          return;
        }
        body.client_id = primaryClientId;
        r = await axios.post(`${API}/admin/immeuble/${masterId}/buildings`, body, { headers: authH() });
        toast.success("Immeuble créé");
      } else {
        r = await axios.put(`${API}/admin/immeuble/${masterId}/buildings/${building.id}`, body, { headers: authH() });
        toast.success("Immeuble mis à jour");
      }
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title={isCreate ? "Nouvel immeuble" : "Modifier l'immeuble"} icon={Building2} tone="blue" onClose={onClose} onSubmit={submit} busy={busy} testid="edit-building-modal">
      <Field label="Nom de l'immeuble" span2>
        <input type="text" value={form.name} onChange={(e) => upd("name", e.target.value)} className="cc-input" maxLength={200} data-testid="edit-building-name" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Adresse" span2>
          <input type="text" value={form.address_line} onChange={(e) => upd("address_line", e.target.value)} className="cc-input" maxLength={300} data-testid="edit-building-address" />
        </Field>
        <Field label="Ville">
          <input type="text" value={form.city} onChange={(e) => upd("city", e.target.value)} className="cc-input" maxLength={100} />
        </Field>
        <Field label="Code postal">
          <input type="text" value={form.postal_code} onChange={(e) => upd("postal_code", e.target.value)} className="cc-input font-mono" maxLength={10} />
        </Field>
        <Field label="Province">
          <select value={form.province} onChange={(e) => upd("province", e.target.value)} className="cc-input">
            <option value="QC">QC</option>
            <option value="ON">ON</option>
            <option value="NB">NB</option>
            <option value="NS">NS</option>
            <option value="MB">MB</option>
            <option value="AB">AB</option>
            <option value="BC">BC</option>
          </select>
        </Field>
        <Field label="Type">
          <select value={form.property_type} onChange={(e) => upd("property_type", e.target.value)} className="cc-input">
            <option value="">—</option>
            <option value="residentiel">Résidentiel</option>
            <option value="commercial">Commercial</option>
            <option value="mixte">Mixte</option>
            <option value="autre">Autre</option>
          </select>
        </Field>
        <Field label="Nb logements">
          <input type="number" min="0" value={form.nb_units_total} onChange={(e) => upd("nb_units_total", e.target.value)} className="cc-input font-mono" />
        </Field>
        <Field label="Année construction">
          <input type="number" value={form.year_built} onChange={(e) => upd("year_built", e.target.value)} className="cc-input font-mono" min="1800" max="2100" />
        </Field>
      </div>
      <div className="pt-2 border-t">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Propriétaire (au registre foncier)</h4>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nom légal" span2>
            <input type="text" value={form.owner_legal_name} onChange={(e) => upd("owner_legal_name", e.target.value)} className="cc-input" maxLength={200} />
          </Field>
          <Field label="Adresse">
            <input type="text" value={form.owner_address_line} onChange={(e) => upd("owner_address_line", e.target.value)} className="cc-input" maxLength={300} />
          </Field>
          <Field label="Ville">
            <input type="text" value={form.owner_city} onChange={(e) => upd("owner_city", e.target.value)} className="cc-input" maxLength={100} />
          </Field>
        </div>
      </div>
      <Field label="Notes" span2>
        <textarea rows={3} value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="cc-input" maxLength={2000} />
      </Field>
    </ModalShell>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// EDIT UNIT
// ════════════════════════════════════════════════════════════════════════════
export function EditUnitModal({ masterId, unit, buildingId, buildings, onClose, onSaved }) {
  const isCreate = !unit;
  const [form, setForm] = useState({
    unit_label: unit?.unit_label || "",
    unit_type: unit?.unit_type || "4.5",
    nb_rooms: unit?.nb_rooms ?? "",
    surface_sqft: unit?.surface_sqft ?? "",
    monthly_rent: unit?.monthly_rent ?? unit?.rent_amount ?? "",
    occupancy_status: unit?.occupancy_status || "vacant",
    has_balcony: !!unit?.has_balcony,
    has_parking: !!unit?.has_parking,
    has_storage: !!unit?.has_storage,
    parking_count: unit?.parking_count ?? "",
    parking_monthly_cost: unit?.parking_monthly_cost ?? "",
    storage_locker_number: unit?.storage_locker_number || "",
    furnishing_state: unit?.furnishing_state || "non_meuble",
    notes: unit?.notes || "",
  });
  const [targetBuildingId, setTargetBuildingId] = useState(buildingId || unit?.property_id || "");
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (isCreate && !form.unit_label.trim()) {
      toast.error("L'étiquette du logement est requise");
      return;
    }
    if (isCreate && !targetBuildingId) {
      toast.error("Choisir un immeuble parent");
      return;
    }
    setBusy(true);
    try {
      const body = { ...form };
      ["nb_rooms", "surface_sqft", "monthly_rent", "parking_count", "parking_monthly_cost"].forEach((k) => {
        if (body[k] === "") delete body[k];
        else body[k] = Number(body[k]);
      });
      Object.keys(body).forEach((k) => { if (body[k] === "") delete body[k]; });
      let r;
      if (isCreate) {
        body.property_id = targetBuildingId;  // Pydantic UnitCreate requiert property_id (override côté backend par sécurité)
        r = await axios.post(`${API}/admin/immeuble/${masterId}/buildings/${targetBuildingId}/units`, body, { headers: authH() });
        toast.success("Logement créé");
      } else {
        r = await axios.put(`${API}/admin/immeuble/${masterId}/units/${unit.id}`, body, { headers: authH() });
        toast.success("Logement mis à jour");
      }
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title={isCreate ? "Nouveau logement" : `Modifier logement ${unit?.unit_label || ""}`} icon={Home} tone="emerald" onClose={onClose} onSubmit={submit} busy={busy} testid="edit-unit-modal">
      {isCreate && (
        <Field label="Immeuble parent" span2>
          <select value={targetBuildingId} onChange={(e) => setTargetBuildingId(e.target.value)} className="cc-input" data-testid="edit-unit-building-select">
            <option value="">— Choisir —</option>
            {(buildings || []).map((b) => (
              <option key={b.id} value={b.id}>{b.name || b.address_line || b.id.slice(0, 8)}</option>
            ))}
          </select>
        </Field>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="N° / Étiquette">
          <input type="text" value={form.unit_label} onChange={(e) => upd("unit_label", e.target.value)} className="cc-input" maxLength={20} data-testid="edit-unit-label" />
        </Field>
        <Field label="Type (ex: 4 1/2)">
          <input type="text" value={form.unit_type} onChange={(e) => upd("unit_type", e.target.value)} className="cc-input" maxLength={20} placeholder="3 1/2, 4 1/2, ..." />
        </Field>
        <Field label="Nombre de pièces (nb_rooms)">
          <input type="number" min="0" step="0.5" value={form.nb_rooms} onChange={(e) => upd("nb_rooms", e.target.value)} className="cc-input font-mono" />
        </Field>
        <Field label="Surface (pi²)">
          <input type="number" min="0" value={form.surface_sqft} onChange={(e) => upd("surface_sqft", e.target.value)} className="cc-input font-mono" />
        </Field>
        <Field label="Loyer mensuel ($)">
          <input type="number" step="0.01" min="0" value={form.monthly_rent} onChange={(e) => upd("monthly_rent", e.target.value)} className="cc-input font-mono" data-testid="edit-unit-rent" />
        </Field>
        <Field label="Statut occupation">
          <select value={form.occupancy_status} onChange={(e) => upd("occupancy_status", e.target.value)} className="cc-input">
            <option value="vacant">Vacant</option>
            <option value="occupied">Occupé</option>
            <option value="renovating">En rénovation</option>
            <option value="reserved">Réservé</option>
          </select>
        </Field>
        <Field label="État meublé">
          <select value={form.furnishing_state} onChange={(e) => upd("furnishing_state", e.target.value)} className="cc-input">
            <option value="">—</option>
            <option value="non_meuble">Non meublé</option>
            <option value="semi_meuble">Semi-meublé</option>
            <option value="meuble">Meublé</option>
          </select>
        </Field>
        <Field label="N° casier rangement">
          <input type="text" value={form.storage_locker_number} onChange={(e) => upd("storage_locker_number", e.target.value)} className="cc-input" maxLength={20} />
        </Field>
        <Field label="Nb places stationnement">
          <input type="number" min="0" value={form.parking_count} onChange={(e) => upd("parking_count", e.target.value)} className="cc-input font-mono" />
        </Field>
        <Field label="Stationnement $/mois">
          <input type="number" step="0.01" min="0" value={form.parking_monthly_cost} onChange={(e) => upd("parking_monthly_cost", e.target.value)} className="cc-input font-mono" />
        </Field>
      </div>
      <div className="flex items-center gap-4 text-xs">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={form.has_balcony} onChange={(e) => upd("has_balcony", e.target.checked)} /> Balcon
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={form.has_parking} onChange={(e) => upd("has_parking", e.target.checked)} /> Stationnement
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={form.has_storage} onChange={(e) => upd("has_storage", e.target.checked)} /> Rangement
        </label>
      </div>
      <Field label="Notes" span2>
        <textarea rows={3} value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="cc-input" maxLength={2000} />
      </Field>
    </ModalShell>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// EDIT / CREATE TENANT
// ════════════════════════════════════════════════════════════════════════════
export function EditTenantModal({ masterId, tenant, unitId, units, unitsById, buildingsById, onClose, onSaved }) {
  const isCreate = !tenant;
  const [form, setForm] = useState({
    first_name: tenant?.first_name || "",
    last_name: tenant?.last_name || "",
    email: tenant?.email || "",
    phone: tenant?.phone || "",
    previous_address: tenant?.previous_address || "",
    emergency_contact_name: tenant?.emergency_contact_name || "",
    emergency_contact_phone: tenant?.emergency_contact_phone || "",
    rent_amount: tenant?.rent_amount ?? "",
    is_active: tenant?.is_active !== false,
    notify_by_sms: !!tenant?.notify_by_sms,
    notes: tenant?.notes || "",
  });
  const [targetUnitId, setTargetUnitId] = useState(unitId || tenant?.unit_id || "");
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (isCreate && (!form.first_name.trim() || !form.last_name.trim())) {
      toast.error("Prénom et nom sont requis");
      return;
    }
    if (isCreate && !targetUnitId) {
      toast.error("Choisir un logement pour le locataire");
      return;
    }
    setBusy(true);
    try {
      const body = { ...form };
      if (body.rent_amount === "") delete body.rent_amount;
      else body.rent_amount = Number(body.rent_amount);
      Object.keys(body).forEach((k) => { if (body[k] === "") delete body[k]; });
      let r;
      if (isCreate) {
        r = await axios.post(`${API}/admin/immeuble/${masterId}/units/${targetUnitId}/tenants`, body, { headers: authH() });
        toast.success("Locataire créé");
      } else {
        r = await axios.put(`${API}/admin/immeuble/${masterId}/tenants/${tenant.id}`, body, { headers: authH() });
        toast.success("Locataire mis à jour");
      }
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const unitLabel = (u) => {
    const b = buildingsById?.[u.property_id];
    const bname = b?.name || b?.address_line || "";
    return `Apt ${u.unit_label || u.id.slice(0, 6)}${bname ? ` — ${bname}` : ""}`;
  };

  return (
    <ModalShell
      title={isCreate ? "Nouveau locataire" : `Modifier locataire ${tenant?.first_name || ""} ${tenant?.last_name || ""}`}
      icon={User} tone="indigo" onClose={onClose} onSubmit={submit} busy={busy} testid="edit-tenant-modal"
    >
      {isCreate && (
        <Field label="Logement parent" span2>
          <select
            value={targetUnitId}
            onChange={(e) => setTargetUnitId(e.target.value)}
            className="cc-input"
            data-testid="edit-tenant-unit-select"
          >
            <option value="">— Choisir un logement —</option>
            {(units || []).map((u) => (
              <option key={u.id} value={u.id}>{unitLabel(u)}</option>
            ))}
          </select>
        </Field>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Prénom">
          <input type="text" value={form.first_name} onChange={(e) => upd("first_name", e.target.value)} className="cc-input" maxLength={100} data-testid="edit-tenant-first-name" />
        </Field>
        <Field label="Nom">
          <input type="text" value={form.last_name} onChange={(e) => upd("last_name", e.target.value)} className="cc-input" maxLength={100} data-testid="edit-tenant-last-name" />
        </Field>
        <Field label="Courriel">
          <input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} className="cc-input" maxLength={120} data-testid="edit-tenant-email" />
        </Field>
        <Field label="Téléphone">
          <input type="tel" value={form.phone} onChange={(e) => upd("phone", e.target.value)} className="cc-input" maxLength={30} data-testid="edit-tenant-phone" />
        </Field>
        <Field label="Adresse précédente" span2>
          <input type="text" value={form.previous_address} onChange={(e) => upd("previous_address", e.target.value)} className="cc-input" maxLength={300} />
        </Field>
        <Field label="Contact d'urgence — Nom">
          <input type="text" value={form.emergency_contact_name} onChange={(e) => upd("emergency_contact_name", e.target.value)} className="cc-input" maxLength={200} />
        </Field>
        <Field label="Contact d'urgence — Téléphone">
          <input type="tel" value={form.emergency_contact_phone} onChange={(e) => upd("emergency_contact_phone", e.target.value)} className="cc-input" maxLength={30} />
        </Field>
        <Field label="Loyer ($)">
          <input type="number" step="0.01" min="0" value={form.rent_amount} onChange={(e) => upd("rent_amount", e.target.value)} className="cc-input font-mono" data-testid="edit-tenant-rent" />
        </Field>
        <Field label="Statut">
          <select value={form.is_active ? "active" : "inactive"} onChange={(e) => upd("is_active", e.target.value === "active")} className="cc-input">
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
          </select>
        </Field>
      </div>
      <label className="flex items-center gap-2 text-xs cursor-pointer pt-1">
        <input type="checkbox" checked={form.notify_by_sms} onChange={(e) => upd("notify_by_sms", e.target.checked)} data-testid="edit-tenant-sms" />
        <span>📱 Recevoir les notifications SMS (opt-in locataire)</span>
      </label>
      <Field label="Notes" span2>
        <textarea rows={3} value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="cc-input" maxLength={2000} />
      </Field>
    </ModalShell>
  );
}
